import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AgentState, AgentStateStatus } from "../types/chat";
import { useApi } from "./use-api";
import { type ApiMutationOptions, useApiMutation, type WithDynamicUrl } from "./use-api-mutation";
import { useApiQuery } from "./use-api-query";

export interface QAManagerRunRequest {
  user_request: string;
  session_id?: string;
  model_name?: string;
}

export interface QAManagerRunResponse {
  session_id: string;
  workflow_id?: string;
  status: string;
  message: string;
}

export interface QAManagerStatusResponse {
  session_id: string;
  workflow_id?: string;
  workflow_status: AgentStateStatus;
  status: AgentStateStatus;
  state: AgentState;
  created_at: string;
  updated_at?: string;
}

export interface QAManagerFileResponse {
  id: string;
  name: string;
  contents: string;
  created_at: string;
  updated_at?: string;
}

export const useQAManagerRun = (
  opts?: ApiMutationOptions<QAManagerRunRequest, QAManagerRunResponse>,
) => {
  const queryClient = useQueryClient();

  return useApiMutation<QAManagerRunRequest, QAManagerRunResponse>("POST", "/qa-manager/run", {
    ...opts,
    onSuccess: (data, s, d) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: [`/qa-manager/sessions/${data.session_id}/status`],
      });
      queryClient.invalidateQueries({ queryKey: ["/agent-sessions"] });

      opts?.onSuccess?.(data, s, d);
    },
  });
};

export const useQAManagerStop = () => {
  const queryClient = useQueryClient();

  return useApiMutation<
    WithDynamicUrl<{ sessionId: string }>,
    { message: string; timestamp: string }
  >("POST", "", {
    onSuccess: (_, variables) => {
      // Invalidate relevant queries to refresh status
      queryClient.invalidateQueries({
        queryKey: [`/qa-manager/sessions/${variables.sessionId}/status`],
      });
      queryClient.invalidateQueries({ queryKey: ["/agent-sessions"] });
    },
  });
};

export const useQAManagerStatus = (sessionId?: string) => {
  return useApiQuery<QAManagerStatusResponse>(`/qa-manager/sessions/${sessionId}/status`, {
    enabled: !!sessionId,
  });
};

export const useQAManagerFiles = (sessionId?: string) => {
  return useApiQuery<QAManagerFileResponse[]>(`/qa-manager/sessions/${sessionId}/files`, {
    enabled: !!sessionId,
  });
};

export const useQAManagerFile = (sessionId?: string, fileId?: string) => {
  return useApiQuery<QAManagerFileResponse>(`/qa-manager/sessions/${sessionId}/files/${fileId}`, {
    enabled: !!sessionId && !!fileId,
  });
};

export const useQAManagerUpdateFile = () => {
  const queryClient = useQueryClient();
  const { callApi } = useApi<QAManagerFileResponse>();

  return useMutation<
    QAManagerFileResponse,
    Error,
    { sessionId: string; fileId: string; contents: string }
  >({
    mutationFn: async ({ sessionId, fileId, contents }) => {
      return callApi(`/qa-manager/sessions/${sessionId}/files/${fileId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "text/plain",
        },
        body: contents,
      });
    },
    onSuccess: (_, variables) => {
      // Invalidate both the specific file and the files list
      queryClient.invalidateQueries({
        queryKey: [`/qa-manager/sessions/${variables.sessionId}/files/${variables.fileId}`],
      });
      queryClient.invalidateQueries({
        queryKey: [`/qa-manager/sessions/${variables.sessionId}/files`],
      });
    },
  });
};
